/**
 * Offline Store
 * ==============
 * Zustand store for offline support and data synchronization.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Chart, Patient } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

interface PendingChange {
  id: string;
  type: 'chart_update' | 'chart_create' | 'patient_update';
  data: unknown;
  timestamp: number;
  retryCount: number;
}

interface OfflineState {
  // Connection status
  isOnline: boolean;
  lastOnlineAt: Date | null;
  
  // Cached data
  cachedCharts: Record<string, Chart>;
  cachedPatients: Record<string, Patient>;
  
  // Pending changes (to sync when back online)
  pendingChanges: PendingChange[];
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: string | null;
  
  // Actions
  setOnline: (online: boolean) => void;
  cacheChart: (chart: Chart) => void;
  cachePatient: (patient: Patient) => void;
  getCachedChart: (chartId: string) => Chart | null;
  getCachedPatient: (patientId: string) => Patient | null;
  
  // Pending changes management
  addPendingChange: (change: Omit<PendingChange, 'id' | 'timestamp' | 'retryCount'>) => void;
  removePendingChange: (changeId: string) => void;
  incrementRetryCount: (changeId: string) => void;
  
  // Sync
  syncPendingChanges: () => Promise<void>;
  clearCache: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useOfflineStore = create<OfflineState>()(
  persist(
    (set, get) => ({
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      lastOnlineAt: null,
      cachedCharts: {},
      cachedPatients: {},
      pendingChanges: [],
      isSyncing: false,
      lastSyncAt: null,
      syncError: null,

      setOnline: (online: boolean) => {
        set({
          isOnline: online,
          lastOnlineAt: online ? new Date() : get().lastOnlineAt,
        });
        
        // Auto-sync when coming back online
        if (online && get().pendingChanges.length > 0) {
          get().syncPendingChanges();
        }
      },

      cacheChart: (chart: Chart) => {
        set((state) => ({
          cachedCharts: {
            ...state.cachedCharts,
            [chart.id]: chart,
          },
        }));
      },

      cachePatient: (patient: Patient) => {
        set((state) => ({
          cachedPatients: {
            ...state.cachedPatients,
            [patient.id]: patient,
          },
        }));
      },

      getCachedChart: (chartId: string) => {
        return get().cachedCharts[chartId] || null;
      },

      getCachedPatient: (patientId: string) => {
        return get().cachedPatients[patientId] || null;
      },

      addPendingChange: (change) => {
        const newChange: PendingChange = {
          ...change,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now(),
          retryCount: 0,
        };
        
        set((state) => ({
          pendingChanges: [...state.pendingChanges, newChange],
        }));
      },

      removePendingChange: (changeId: string) => {
        set((state) => ({
          pendingChanges: state.pendingChanges.filter((c) => c.id !== changeId),
        }));
      },

      incrementRetryCount: (changeId: string) => {
        set((state) => ({
          pendingChanges: state.pendingChanges.map((c) =>
            c.id === changeId ? { ...c, retryCount: c.retryCount + 1 } : c
          ),
        }));
      },

      syncPendingChanges: async () => {
        const state = get();
        
        if (!state.isOnline || state.isSyncing || state.pendingChanges.length === 0) {
          return;
        }
        
        set({ isSyncing: true, syncError: null });
        
        const maxRetries = 3;
        const failedChanges: PendingChange[] = [];
        
        for (const change of state.pendingChanges) {
          if (change.retryCount >= maxRetries) {
            failedChanges.push(change);
            continue;
          }
          
          try {
            await processPendingChange(change);
            get().removePendingChange(change.id);
          } catch (error) {
            console.error('Failed to sync change:', change.id, error);
            get().incrementRetryCount(change.id);
            failedChanges.push(change);
          }
        }
        
        set({
          isSyncing: false,
          lastSyncAt: new Date(),
          syncError: failedChanges.length > 0
            ? `Failed to sync ${failedChanges.length} changes`
            : null,
        });
      },

      clearCache: () => {
        set({
          cachedCharts: {},
          cachedPatients: {},
          pendingChanges: [],
        });
      },
    }),
    {
      name: 'perio-chart-offline',
      partialize: (state) => ({
        cachedCharts: state.cachedCharts,
        cachedPatients: state.cachedPatients,
        pendingChanges: state.pendingChanges,
        lastSyncAt: state.lastSyncAt,
      }),
    }
  )
);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

async function processPendingChange(change: PendingChange): Promise<void> {
  const { autosaveChart, createChart, updateChart } = await import('@/api/charts');
  const { updatePatient } = await import('@/api/patients');
  
  switch (change.type) {
    case 'chart_update': {
      const data = change.data as { chartId: string; updates: unknown };
      await autosaveChart(data.chartId, data.updates as any);
      break;
    }
    case 'chart_create': {
      await createChart(change.data as any);
      break;
    }
    case 'patient_update': {
      const data = change.data as { patientId: string; updates: unknown };
      await updatePatient(data.patientId, data.updates as any);
      break;
    }
    default:
      console.warn('Unknown change type:', change.type);
  }
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to set up online/offline event listeners.
 */
export function useOnlineStatus() {
  const setOnline = useOfflineStore((state) => state.setOnline);
  
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => setOnline(true));
    window.addEventListener('offline', () => setOnline(false));
  }
  
  return useOfflineStore((state) => ({
    isOnline: state.isOnline,
    lastOnlineAt: state.lastOnlineAt,
    pendingChangesCount: state.pendingChanges.length,
    isSyncing: state.isSyncing,
  }));
}

/**
 * Hook to get chart with offline fallback.
 */
export function useCachedChart(chartId: string | null) {
  return useOfflineStore((state) => {
    if (!chartId) return null;
    return state.cachedCharts[chartId] || null;
  });
}

/**
 * Hook to check if there are unsaved changes.
 */
export function useHasPendingChanges() {
  return useOfflineStore((state) => state.pendingChanges.length > 0);
}
